<template>
    <div v-if="isOpen" class="sidebar-backdrop" aria-hidden="true" @click="closeSidebar"></div>

    <aside
        id="admin-sidebar"
        class="sidebar-wrapper"
        :class="{ 'is-open': isOpen, 'is-collapsed': collapsed }"
    >
        <div class="sidebar-brand-wrapper">
            <router-link to="/dashboard" class="sidebar-brand" aria-label="IPC Admin dashboard" @click="closeSidebar">
                <span class="sidebar-brand-mark" aria-hidden="true">IP</span>
                <span class="sidebar-brand-text">IPC Admin</span>
            </router-link>
        </div>

        <nav class="sidebar-content" aria-label="Admin navigation">
            <ul class="sidebar-menu">
                <li v-for="group in menuGroups" :key="group.label" class="sidebar-group">
                    <p class="sidebar-heading">{{ group.label }}</p>
                    <ul class="sidebar-group-links">
                        <li v-for="item in group.items" :key="item.to">
                            <router-link
                                :to="item.to"
                                class="nav-link"
                                :class="{ 'is-active': isActive(item.to) }"
                                :aria-current="isActive(item.to) ? 'page' : null"
                                :aria-label="item.label"
                                :title="collapsed ? item.label : null"
                                @click="closeSidebar"
                            >
                                <i :class="['pi', item.icon]" aria-hidden="true"></i>
                                <span>{{ item.label }}</span>
                            </router-link>
                        </li>
                    </ul>
                </li>
            </ul>
        </nav>
    </aside>
</template>

<script>
import {defineComponent} from 'vue'

export default defineComponent({
    name: 'Sidebar',
    props: {
        isOpen: {
            type: Boolean,
            default: false
        },
        collapsed: {
            type: Boolean,
            default: false
        }
    },
    emits: ['close'],
    data() {
        return {
            menuGroups: [
                {
                    label: 'Overview',
                    items: [
                        {label: 'Dashboard', to: '/dashboard', icon: 'pi-home'}
                    ]
                },
                {
                    label: 'Directory',
                    items: [
                        {label: 'Member', to: '/member', icon: 'pi-users'},
                        {label: 'Add Member', to: '/member/add', icon: 'pi-user-plus'},
                        {label: 'Import Members', to: '/member/import', icon: 'pi-file-excel'},
                        {label: 'Member Sync', to: '/member/sync', icon: 'pi-sync'},
                        {label: 'Speaker', to: '/speaker', icon: 'pi-user'}
                    ]
                },
                {
                    label: 'Event',
                    items: [
                        {label: 'Active Event', to: '/event-active', icon: 'pi-calendar'},
                        {label: 'Event', to: '/event', icon: 'pi-list'},
                        {label: 'Delegate Question', to: '/forum-question', icon: 'pi-comments'},
                        {label: 'Photo Gallery', to: '/photo-gallery', icon: 'pi-images'},
                        {label: 'Video Gallery', to: '/video-gallery', icon: 'pi-video'}
                    ]
                },
                {
                    label: 'Partners',
                    items: [
                        {label: 'Sponsor', to: '/sponsor', icon: 'pi-star'},
                        {label: 'Exhibitor', to: '/exhibitor', icon: 'pi-briefcase'},
                        {label: 'Place', to: '/place', icon: 'pi-map-marker'}
                    ]
                },
                {
                    label: 'Engagement',
                    items: [
                        {label: 'Questions', to: '/quiz/question', icon: 'pi-question-circle'},
                        {label: 'Quiz', to: '/quiz', icon: 'pi-check-square'},
                        {label: 'Broadcast Notification', to: '/message/broadcast', icon: 'pi-bell'}
                    ]
                },
                {
                    label: 'Reports',
                    items: [
                        {label: 'Speaker & Session Evaluation', to: '/report', icon: 'pi-star'},
                        {label: 'Session Participation', to: '/report/session-participation', icon: 'pi-chart-bar'}
                    ]
                },
                {
                    label: 'System',
                    items: [
                        {label: 'General Setting', to: '/general-setting', icon: 'pi-cog'}
                    ]
                }
            ]
        }
    },
    methods: {
        closeSidebar() {
            this.$emit('close')
        },
        isActive(path) {
            return this.$route.path === path || (path === '/dashboard' && this.$route.path === '/')
        }
    }
})
</script>

<style scoped>
.sidebar-wrapper {
    --sidebar-background-start: #3656bf;
    --sidebar-background-end: #1f3d9b;
    display: flex;
    flex-direction: column;
    height: 100vh;
    height: 100dvh;
    width: 224px;
    position: fixed;
    left: 0;
    top: 0;
    z-index: 1040;
    background: linear-gradient(180deg, var(--sidebar-background-start) 10%, var(--sidebar-background-end) 100%);
}

.sidebar-brand-wrapper {
    flex-shrink: 0;
    border-bottom: 1px solid rgba(255, 255, 255, 0.18);
}

.sidebar-brand {
    min-height: 4.375rem;
    padding: 1rem;
    color: #fff;
    display: flex;
    align-items: center;
    text-decoration: none;
    text-transform: uppercase;
    letter-spacing: 0.08rem;
    font-size: 0.95rem;
    font-weight: 800;
}

.sidebar-brand:hover,
.sidebar-brand:focus {
    color: #fff;
    text-decoration: none;
}

.sidebar-brand-mark {
    display: none;
}

.sidebar-content {
    flex: 1;
    overflow-y: auto;
    overflow-x: hidden;
    padding: 1rem 0 1.5rem;
}

.sidebar-menu,
.sidebar-group-links {
    list-style: none;
    padding: 0;
    margin: 0;
}

.sidebar-group + .sidebar-group {
    margin-top: 1.25rem;
}

.sidebar-heading {
    margin: 0 1rem 0.45rem;
    color: #dfe8ff;
    font-size: 0.68rem;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
}

.nav-link {
    min-height: 44px;
    padding: 0.68rem 1rem;
    display: flex;
    align-items: center;
    gap: 0.75rem;
    border-left: 3px solid transparent;
    color: #f8f9ff;
    font-size: 0.9rem;
    font-weight: 500;
    line-height: 1.25;
    text-decoration: none;
    transition: background-color 160ms ease, color 160ms ease, border-color 160ms ease;
}

.nav-link:hover {
    color: #fff;
    background: rgba(255, 255, 255, 0.12);
    text-decoration: none;
}

.nav-link.is-active {
    color: #fff;
    border-left-color: #fff;
    background: rgba(255, 255, 255, 0.2);
    font-weight: 700;
}

.nav-link:focus-visible,
.sidebar-brand:focus-visible {
    outline: 3px solid #fff;
    outline-offset: -3px;
}

.nav-link .pi {
    width: 1.25rem;
    color: currentColor;
    font-size: 1rem;
    text-align: center;
}

.sidebar-content::-webkit-scrollbar {
    width: 6px;
}

.sidebar-content::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.45);
    border-radius: 3px;
}

.sidebar-backdrop {
    display: none;
}

@media (min-width: 769px) {
    .sidebar-wrapper {
        transition: width 200ms ease;
    }

    .sidebar-wrapper.is-collapsed {
        width: 72px;
    }

    .sidebar-wrapper.is-collapsed .sidebar-brand {
        justify-content: center;
        padding: 1rem 0.5rem;
    }

    .sidebar-wrapper.is-collapsed .sidebar-brand-text,
    .sidebar-wrapper.is-collapsed .sidebar-heading,
    .sidebar-wrapper.is-collapsed .nav-link span {
        display: none;
    }

    .sidebar-wrapper.is-collapsed .sidebar-brand-mark {
        display: block;
        font-size: 0.88rem;
        font-weight: 800;
        letter-spacing: 0.04em;
    }

    .sidebar-wrapper.is-collapsed .sidebar-group + .sidebar-group {
        margin-top: 0.5rem;
    }

    .sidebar-wrapper.is-collapsed .nav-link {
        justify-content: center;
        padding: 0.68rem 0.5rem;
    }

    .sidebar-wrapper.is-collapsed .nav-link .pi {
        width: auto;
    }
}

@media (max-width: 768px) {
    .sidebar-wrapper {
        box-shadow: 0 0.75rem 2rem rgba(18, 35, 92, 0.35);
        transform: translateX(-100%);
        transition: transform 200ms ease;
    }

    .sidebar-wrapper.is-open {
        transform: translateX(0);
    }

    .sidebar-backdrop {
        display: block;
        position: fixed;
        inset: 0;
        z-index: 1035;
        background: rgba(13, 22, 48, 0.45);
    }
}

@media (prefers-reduced-motion: reduce) {
    .sidebar-wrapper,
    .nav-link {
        transition: none;
    }
}
</style>
